import type { Filter as IdentitySectionFilter } from '@/components/Filter/IdentitySectionFilter'
import type { Filter as IncomeSectionFilter } from '@/components/Filter/IncomeSectionFilter'
import type { Filter as SectionFilter } from '@/components/Filter/SectionFilter'
import type {
  QueryVars as OutcomeReviewQueryVars,
  Filters as OutcomeReviewFilterQueryVars,
  IdentitySection as IdentitySectionQueryVars,
  IncomeSection as IncomeSectionQueryVars,
  Section as SectionQueryVars,
} from '@/lib/apollo/query/get-all-subjects'
import type {
  QueryVars as JourneyProgressQueryVars,
  Filters as JourneyProgressFilterQueryVars,
} from '@/lib/apollo/query/get-journey-progress-subjects'

import type { FilterT as JourneyProgressFilterT } from './JourneyProgressFilter'
import type { SearchValues as JourneyProgressSearchValues } from './JourneyProgressSearchPanel'
import type { FilterT as OutcomeReviewFilterT } from './OutcomeReviewFilter'
import type { SearchValues as OutcomeReviewSearchValues } from './OutcomeReviewSearchPanel'

export const hasAppliedFilters = ({ needsReview, reopened }: SectionFilter): boolean =>
  needsReview || reopened

const toSectionQueryVars = (section: SectionFilter): SectionQueryVars =>
  !hasAppliedFilters(section)
    ? {}
    : {
        ...(section.needsReview
          ? {
              needsReview: section.needsReview,
            }
          : {}),
        ...(section.reopened
          ? {
              reopened: section.reopened,
            }
          : {}),
      }

const toIdentitySectionQueryVars = (
  identitySection: IdentitySectionFilter,
): IdentitySectionQueryVars => ({
  ...(!hasAppliedFilters(identitySection.scottishWelsh)
    ? {}
    : {
        scottishWelsh: toSectionQueryVars(identitySection.scottishWelsh),
      }),
  ...(!hasAppliedFilters(identitySection.other)
    ? {}
    : {
        other: toSectionQueryVars(identitySection.other),
      }),
})

const toIncomeSectionQueryVars = (
  incomeSection: IncomeSectionFilter,
): IncomeSectionQueryVars => ({
  ...(!hasAppliedFilters(incomeSection.paye)
    ? {}
    : {
        paye: toSectionQueryVars(incomeSection.paye),
      }),
  ...(!hasAppliedFilters(incomeSection.other)
    ? {}
    : {
        other: toSectionQueryVars(incomeSection.other),
      }),
})

const toOutcomeReviewFilterQueryVars = (
  filter: OutcomeReviewFilterT,
): OutcomeReviewFilterQueryVars => ({
  queueSettings: filter.queueSettings,
  identity: toIdentitySectionQueryVars(filter.identity),
  credit: toSectionQueryVars(filter.credit),
  income: toIncomeSectionQueryVars(filter.income),
  residential: toSectionQueryVars(filter.residential),
  applicantType: filter.applicantType,
  product: filter.product,
  noOwner: filter.noOwner,
  referenceGroup: filter.referenceGroup,
  swimLaneGroup: filter.swimLaneGroup,
  fraud: filter.fraud,
  tenancyStartDate: filter.tenancyStartDate,
  ageOfReference: filter.ageOfReference,
  sla: filter.sla,
  additionalInfo: filter.additionalInfo,
})

interface ToOutcomeReviewQueryVarsArgs {
  customerId: string
  limit: number
  offset: number
  searchValues: OutcomeReviewSearchValues
}

export const toOutcomeReviewQueryVars = ({
  customerId,
  limit,
  offset,
  searchValues,
}: ToOutcomeReviewQueryVarsArgs): OutcomeReviewQueryVars => ({
  customerId,
  filter: toOutcomeReviewFilterQueryVars(searchValues.filter),
  limit,
  offset,
})

const toJourneyProgressFilterQueryVars = (
  filter: JourneyProgressFilterT,
): JourneyProgressFilterQueryVars => ({
  webform: filter.webform,
  journeyStage: filter.journeyStage,
  applicantType: filter.applicantType,
  product: filter.product,
  noOwner: filter.noOwner,
  referenceGroup: filter.referenceGroup,
  swimLaneGroup: filter.swimLaneGroup,
  fraud: filter.fraud,
  tenancyStartDate: filter.tenancyStartDate,
  sla: filter.sla,
  additionalInfo: filter.additionalInfo,
})

interface ToJourneyProgressQueryVarsArgs {
  customerId: string
  limit: number
  offset: number
  searchValues: JourneyProgressSearchValues
}

export const toJourneyProgressQueryVars = ({
  customerId,
  limit,
  offset,
  searchValues,
}: ToJourneyProgressQueryVarsArgs): JourneyProgressQueryVars => ({
  customerId,
  filter: toJourneyProgressFilterQueryVars(searchValues.filter),
  limit,
  offset,
})
